import { PriceStatus } from '@toggle/helpers';
import clsx from 'clsx';
import { formatInTimeZone } from 'date-fns-tz';
import { t } from 'i18next';
import React from 'react';

import { useFlags } from '~/stores/use-flags/useFlags';

import { formatProportionChange } from '../utils';
import styles from './PriceSummaryDisplay.module.scss';

export interface PriceColumnConfig {
  price?: string;
  currency: string;
  label?: React.ReactNode;
}

export interface PriceSummaryDisplayProps {
  priceColumn?: PriceColumnConfig;
  percentageChange?: number;
  prevCloseTime?: string;
}

const priceStatusClassMap: Record<PriceStatus, string> = {
  [PriceStatus.Default]: styles.default,
  [PriceStatus.Negative]: styles.negative,
  [PriceStatus.Positive]: styles.positive,
};

export const PriceSummaryDisplay = ({
  priceColumn,
  percentageChange,
  prevCloseTime,
}: PriceSummaryDisplayProps) => {
  const isUniversalDrawerEnabled = useFlags(state =>
    state.getFeatureFlagState('universal_drawer')
  );

  if (!priceColumn || priceColumn.price === undefined) {
    return null;
  }

  return (
    <>
      <div
        className={clsx(styles.proportionChangeColumn, {
          [styles.proportionChangeColumnV2]: isUniversalDrawerEnabled,
        })}
      >
        <div className={styles.priceRow}>
          <span className={styles.proportionPrice}>{priceColumn.price}</span>
          <span className={styles.proportionCurrency}>
            {priceColumn.currency}
          </span>
        </div>
        {priceColumn.label}
      </div>
      {percentageChange !== undefined && (
        <div
          className={clsx(styles.proportionChangeColumn, {
            [styles.proportionChangeColumnV2]: isUniversalDrawerEnabled,
          })}
        >
          <span
            className={clsx(
              styles.percentageChange,
              priceStatusClassMap[
                percentageChange > 0
                  ? PriceStatus.Positive
                  : percentageChange < 0
                  ? PriceStatus.Negative
                  : PriceStatus.Default
              ]
            )}
          >
            {formatProportionChange(percentageChange)}
          </span>
          {prevCloseTime && (
            <span
              className={clsx(styles.dateSpan, {
                [styles.dateSpanV2]: isUniversalDrawerEnabled,
              })}
            >
              {t('analyze:analysis.sinceDateClose', {
                date: formatInTimeZone(prevCloseTime, 'UTC', 'MMM d'),
              })}
            </span>
          )}
        </div>
      )}
    </>
  );
};
